import { useLocation } from 'react-router-dom';
import { Compass, ShieldOff, ServerCog, WifiOff } from 'lucide-react';
import SystemPage from '@parsina/framework/clinical/src/components/system/SystemPage';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { logout } from '../../lib/auth';

// ─────────────────────────────────────────────────────────────────────────────
// The routed system states.
//
// These are pages a doctor can LAND on — by following a stale bookmark, a link
// a colleague sent, a QR code from a printout, or a redirect the app itself
// performed. That distinguishes them from the inline states (`ErrorState`,
// `EmptyState`), which only ever appear inside a screen the doctor deliberately
// opened.
//
// The rule that shapes all of them: never lie about where the doctor is. Before
// this, '*' redirected silently to پذیرش, so a mistyped URL, a route removed in
// a refactor, and a link to a page that requires a role the doctor lacks were
// all indistinguishable from "you asked for the reception desk". The doctor's
// bookmark appeared to work while quietly taking them somewhere else, and the
// bad link was never reported because nobody could tell it was bad.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 404 — the address does not correspond to anything.
 *
 * Shows the path it could not resolve. That is not developer noise: the doctor
 * is the one holding the broken link, and the path is the only thing that lets
 * them see the typo, or paste something useful into a support message.
 */
export function NotFound() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const location = useLocation();

  return (
    <SystemPage
      testId="not-found"
      tone="neutral"
      code="404"
      icon={<Compass size={30} strokeWidth={1.8} aria-hidden />}
      title={t('errors.routes.notFoundTitle')}
      body={t('errors.routes.notFoundBody')}
      // The pathname only — never the query string, which on this app can carry
      // a conversation id or an auth code.
      detail={location.pathname}
      actions={[
        { key: 'reception', label: t('errors.routes.goReception'), onClick: () => navigate('reception') },
        { key: 'back', label: t('common.actions.back'), onClick: () => window.history.back(), variant: 'secondary' },
      ]}
    />
  );
}

/**
 * 403 — authenticated, but not permitted.
 *
 * Deliberately offers no retry. The doctor pressing a button ten times cannot
 * grant themselves a role, and pretending otherwise wastes their time; the real
 * next step is a person, so the copy names one.
 */
export function Forbidden({ detail }) {
  const { t } = useTranslation();
  const navigate = useAppNavigate();

  return (
    <SystemPage
      testId="forbidden"
      tone="warning"
      code="403"
      icon={<ShieldOff size={28} strokeWidth={1.8} aria-hidden />}
      title={t('errors.kind.forbidden.title')}
      body={t('errors.kind.forbidden.body')}
      detail={detail}
      actions={[
        { key: 'reception', label: t('errors.routes.goReception'), onClick: () => navigate('reception') },
        // Signing in as someone else is a legitimate fix — a shared clinic
        // workstation left on the previous doctor's session is the ordinary way
        // to arrive here.
        { key: 'switch', label: t('errors.routes.signInAsAnother'), onClick: () => logout(), variant: 'secondary' },
      ]}
    />
  );
}

/**
 * 503 — a dependency is down or deploying.
 *
 * The one system page with a live retry, because the condition genuinely does
 * clear on its own. `onRetry` defaults to a reload so it is useful even when
 * mounted without one.
 */
export function Maintenance({ detail, onRetry }) {
  const { t } = useTranslation();

  return (
    <SystemPage
      testId="maintenance"
      tone="info"
      code="503"
      icon={<ServerCog size={28} strokeWidth={1.8} aria-hidden />}
      title={t('errors.kind.maintenance.title')}
      body={t('errors.kind.maintenance.body')}
      detail={detail}
      actions={[
        { key: 'retry', label: t('common.actions.retry'), onClick: onRetry || (() => window.location.reload()) },
      ]}
      footer={t('errors.routes.maintenanceFooter')}
    />
  );
}

/**
 * The cold offline boot: the app shell came out of the service-worker cache but
 * there is no network, so no screen can load anything.
 *
 * Distinct from the offline BANNER (Phase 3), which appears over a working
 * screen that already has data on it. This is for when there is nothing to show
 * at all, and it is deliberately honest that the doctor can keep the app open
 * and it will recover by itself.
 */
export function Offline({ onRetry, fullscreen = false }) {
  const { t } = useTranslation();

  return (
    <SystemPage
      testId="offline-page"
      // `fullscreen` when this IS the app — the boot-offline case renders it
      // outside the shell, where there is no chrome to sit inside and nothing to
      // give it a height.
      fullscreen={fullscreen}
      tone="warning"
      icon={<WifiOff size={28} strokeWidth={1.8} aria-hidden />}
      title={t('errors.kind.offline.title')}
      body={t('errors.kind.offline.body')}
      actions={[
        { key: 'retry', label: t('common.actions.retry'), onClick: onRetry || (() => window.location.reload()) },
      ]}
      footer={t('errors.routes.offlineFooter')}
    />
  );
}

// RecordNotFound moved into @parsina/framework/clinical with the patient record
// that renders it; re-exported so this module stays the one place the doctor
// panel's system pages are imported from.
export { RecordNotFound } from '@parsina/framework/clinical/src/screens/System/RecordNotFound';
